import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Folder, Minus } from "lucide-react";

const MIN_W = 520;
const MIN_H = 340;

const HANDLES = [
  { dir: 'n',  style: { top: 0,    left: 4,  right: 4,  height: 6,  cursor: 'n-resize'  } },
  { dir: 's',  style: { bottom: 0, left: 4,  right: 4,  height: 6,  cursor: 's-resize'  } },
  { dir: 'e',  style: { right: 0,  top: 4,   bottom: 4, width: 6,   cursor: 'e-resize'  } },
  { dir: 'w',  style: { left: 0,   top: 4,   bottom: 4, width: 6,   cursor: 'w-resize'  } },
  { dir: 'ne', style: { top: 0,    right: 0, width: 12, height: 12, cursor: 'ne-resize' } },
  { dir: 'nw', style: { top: 0,    left: 0,  width: 12, height: 12, cursor: 'nw-resize' } },
  { dir: 'se', style: { bottom: 0, right: 0, width: 12, height: 12, cursor: 'se-resize' } },
  { dir: 'sw', style: { bottom: 0, left: 0,  width: 12, height: 12, cursor: 'sw-resize' } },
];

const FloatingWindow = ({ open, onClose, title, children }) => {
  const initW = Math.min(980, window.innerWidth - 40);
  const initH = Math.min(680, window.innerHeight - 40);

  const [pos,  setPos]  = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: initW, h: initH });
  const [minimized, setMinimized] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const dragging  = useRef(false);
  const resizing  = useRef(null);
  const dragOff   = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (open && !initialized) {
      const w = Math.min(980, window.innerWidth - 40);
      const h = Math.min(680, window.innerHeight - 40);
      setSize({ w, h });
      setPos({ x: Math.max(20, (window.innerWidth - w) / 2), y: Math.max(20, (window.innerHeight - h) / 3) });
      setInitialized(true);
      setMinimized(false);
    }
    if (!open) setInitialized(false);
  }, [open, initialized]);

  const onDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    dragging.current = true;
    dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  const onResizeStart = useCallback((e, dir) => {
    e.stopPropagation();
    e.preventDefault();
    resizing.current = { dir, startX: e.clientX, startY: e.clientY, startW: size.w, startH: size.h, startPX: pos.x, startPY: pos.y };
  }, [size, pos]);

  useEffect(() => {
    const onMove = (e) => {
      if (dragging.current) {
        setPos({
          x: Math.max(0, Math.min(window.innerWidth  - 100, e.clientX - dragOff.current.x)),
          y: Math.max(0, Math.min(window.innerHeight - 40,  e.clientY - dragOff.current.y)),
        });
        return;
      }
      if (!resizing.current) return;
      const { dir, startX, startY, startW, startH, startPX, startPY } = resizing.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      let nw = startW, nh = startH, nx = startPX, ny = startPY;

      if (dir.includes('e'))  nw = Math.max(MIN_W, startW + dx);
      if (dir.includes('s'))  nh = Math.max(MIN_H, startH + dy);
      if (dir.includes('w')) { nw = Math.max(MIN_W, startW - dx); nx = startPX + (startW - nw); }
      if (dir.includes('n')) { nh = Math.max(MIN_H, startH - dy); ny = startPY + (startH - nh); }

      setSize({ w: nw, h: nh });
      setPos({ x: nx, y: ny });
    };
    const onUp = () => { dragging.current = false; resizing.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, []);

  if (!open) return null;

  return createPortal(
    <div
      data-testid="reports-floating-window"
      style={{
        position: 'fixed', left: pos.x, top: pos.y,
        width: size.w, height: minimized ? 'auto' : size.h,
        zIndex: 9999,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.08)',
      }}
      className="bg-white rounded-xl overflow-hidden flex flex-col select-none"
    >
      {!minimized && HANDLES.map(({ dir, style }) => (
        <div
          key={dir}
          onMouseDown={(e) => onResizeStart(e, dir)}
          style={{ position: 'absolute', ...style, zIndex: 10 }}
          data-testid={`resize-handle-${dir}`}
        />
      ))}

      <div
        onMouseDown={onDragStart}
        className="flex items-center gap-3 px-4 py-3 bg-slate-800 cursor-move select-none shrink-0"
        style={{ WebkitUserSelect: 'none' }}
      >
        <div className="flex items-center gap-1.5">
          <button onMouseDown={e => e.stopPropagation()} onClick={onClose}
            className="w-3 h-3 rounded-full bg-rose-400 hover:bg-rose-500 transition-colors"
            title="Cerrar" data-testid="floating-window-close" />
          <button onMouseDown={e => e.stopPropagation()} onClick={() => setMinimized(m => !m)}
            className="w-3 h-3 rounded-full bg-amber-400 hover:bg-amber-500 transition-colors"
            title={minimized ? "Restaurar" : "Minimizar"} data-testid="floating-window-minimize" />
          <div className="w-3 h-3 rounded-full bg-emerald-400" title="Redimensionable" />
        </div>
        <div className="flex items-center gap-2 text-white flex-1">
          <Folder className="w-4 h-4 text-sky-400" />
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <span className="text-xs text-slate-500 hidden sm:block select-none">
          {Math.round(size.w)} x {Math.round(size.h)} - Arrastrar para mover
        </span>
      </div>

      {!minimized && (
        <div className="flex-1 overflow-auto p-4 flex flex-col gap-3 select-text">
          {children}
        </div>
      )}
    </div>,
    document.body
  );
};

export default FloatingWindow;
